/**
 * RecentActivity — Section 4 of the Office Manager Report.
 *
 * Last N mutation events from the audit_log collection.
 * data-testid: candeza-reports-operations-activity-*
 */

import React from 'react';
import styled from 'styled-components';
import type { ActivityEntry } from '@/api/reports';
import { formatTime } from '@/api/reports';

// ── Styled components ─────────────────────────────────────────────────────────

const List = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0;
`;

const Item = styled.li`
  display: flex;
  gap: ${({ theme }) => theme.spacing.sm};
  padding: ${({ theme }) => `${theme.spacing.xs} 0`};
  border-bottom: 1px solid ${({ theme }) => theme.colors.border};
  font-size: ${({ theme }) => theme.typography.fontSizes.sm};

  &:last-child {
    border-bottom: none;
  }
`;

const Time = styled.span`
  color: ${({ theme }) => theme.colors.textSecondary};
  min-width: 48px;
  flex-shrink: 0;
`;

const Summary = styled.span`
  flex: 1;
  color: ${({ theme }) => theme.colors.text};
`;

const Actor = styled.span`
  color: ${({ theme }) => theme.colors.textSecondary};
  font-style: italic;
`;

const Empty = styled.div`
  color: ${({ theme }) => theme.colors.textSecondary};
  font-style: italic;
  padding: ${({ theme }) => theme.spacing.md} 0;
`;

// ── Component ─────────────────────────────────────────────────────────────────

interface RecentActivityProps {
  entries: ActivityEntry[];
  loading?: boolean;
}

export const RecentActivity: React.FC<RecentActivityProps> = ({ entries, loading }) => {
  if (loading) {
    return <div data-testid="candeza-reports-operations-activity-loading">Loading activity…</div>;
  }

  if (!entries.length) {
    return (
      <Empty data-testid="candeza-reports-operations-activity-empty">
        No recent activity recorded.
      </Empty>
    );
  }

  return (
    <List data-testid="candeza-reports-operations-activity-list">
      {entries.map((e) => (
        <Item key={e.entry_id} data-testid={`candeza-reports-operations-activity-row-${e.entry_id}`}>
          <Time>{formatTime(e.created_at)}</Time>
          <Summary>{e.summary}</Summary>
          <Actor>by {e.user_display}</Actor>
        </Item>
      ))}
    </List>
  );
};

